import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setMonthlyIoncome,
  setCreditPay,
  setCarPay,
  setStudentPay,
  setDebetRatio,
  resetDtiData,
  setTotalMonthlyDebt,
  setRemainingIncome,
} from "../redux/calculations/dtiSlice";
import {
  resetChartData,
  updatedDtiChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { Helmet } from "react-helmet";

const DtiCal = () => {
  const dispatch = useDispatch();
  const { monthlyIncome, creditPay, carPay, studentPay } = useSelector(
    (state) => state.dtiCalculator
  );

  const [calcDone, setCalcDone] = useState(false);
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { textData } = useLocale();
  const { dtiCalc } = textData;

  const formik = useFormik({
    initialValues: {
      monthlyIncome: monthlyIncome,
      creditPay: creditPay,
      carPay: carPay,
      studentPay: studentPay,
    },
    validationSchema: Yup.object({
      monthlyIncome: Yup.number()
        .required(dtiCalc.monthlyIncomeRequired)
        .min(10000, dtiCalc.monthlyIncomeMin)
        .max(500000, dtiCalc.monthlyIncomeMax),

      creditPay: Yup.number()
        .required(dtiCalc.creditPayRequired)
        .min(2000, dtiCalc.creditPayMin)
        .max(100000, dtiCalc.creditPayMax),

      carPay: Yup.number()
        .required(dtiCalc.carPayRequired)
        .min(5000, dtiCalc.carPayMin)
        .max(100000, dtiCalc.carPayMax),

      studentPay: Yup.number()
        .required(dtiCalc.studentPayRequired)
        .min(1000, dtiCalc.studentPayMin)
        .max(100000, dtiCalc.studentPayMax),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_DTI");
      }

      const totalMonthlyDebt =
        Number(values.creditPay) +
        Number(values.carPay) +
        Number(values.studentPay);

      const remainingIncome = Number(values.monthlyIncome) - totalMonthlyDebt;

      const dtiRatio = (totalMonthlyDebt / values.monthlyIncome) * 100;

      dispatch(setMonthlyIoncome(values.monthlyIncome));
      dispatch(setCreditPay(values.creditPay));
      dispatch(setCarPay(values.carPay));
      dispatch(setStudentPay(values.studentPay));
      dispatch(setDebetRatio(dtiRatio));
      dispatch(setTotalMonthlyDebt(totalMonthlyDebt));
      dispatch(setRemainingIncome(remainingIncome));
      dispatch(
        updatedDtiChart({
          totalMonthlyDebt: totalMonthlyDebt,
          remainingIncome: remainingIncome,
        })
      );
      setCalcDone(true);
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetDtiData());
    };
  }, [dispatch]);

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };

  // Create specific input handlers for each field
  const handleMonthlyIncomeChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue === "") {
      formik.setFieldValue("monthlyIncome", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 10) return;

    if (rawValue === "0") {
      formik.setFieldValue("monthlyIncome", 0);
      return;
    }

    formik.setFieldValue("monthlyIncome", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };

  const handleCreditPayChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("creditPay", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 10) return;

    if (rawValue === "0") {
      formik.setFieldValue("creditPay", 0);
      return;
    }

    formik.setFieldValue("creditPay", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };

  const handleCarPayChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("carPay", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 10) return;

    if (rawValue === "0") {
      formik.setFieldValue("carPay", 0);
      return;
    }

    formik.setFieldValue("carPay", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };

  const handleStudentPayChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("studentPay", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 10) return;

    if (rawValue === "0") {
      formik.setFieldValue("studentPay", 0);
      return;
    }

    formik.setFieldValue("studentPay", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };

  const sliderTrackRef = useRef(null);
  const carSliderRef = useRef(null);
  const creditSliderRef = useRef(null);
  const studentSliderRef = useRef(null);

  const handleCarSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setCarPay(value));
    formik.setFieldValue("carPay", value);
    setCalcDone(false);
  };

  const handleCreditSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setCreditPay(value));
    formik.setFieldValue("creditPay", value);
    setCalcDone(false);
  };

  const handleStudentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setStudentPay(value));
    formik.setFieldValue("studentPay", value);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMonthlyIoncome(value));
    formik.setFieldValue("monthlyIncome", value);
    setCalcDone(false);
  };

  useEffect(() => {
    formik.setValues({
      monthlyIncome: monthlyIncome,
      creditPay: creditPay,
      carPay: carPay,
      studentPay: studentPay,
    });
  }, [monthlyIncome, creditPay, carPay, studentPay]);

  useEffect(() => {
    dispatch(setMonthlyIoncome(formik.values.monthlyIncome));
    dispatch(setCreditPay(formik.values.creditPay));
    dispatch(setCarPay(formik.values.carPay));
    dispatch(setStudentPay(formik.values.studentPay));
  }, [formik.values, dispatch]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.debtContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.debtContent.description[0]}
      </p>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.debtContent.description[1]}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.debtContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.debtContent.howItWorksSteps[0]}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.debtContent.howItWorksSteps[1]}
      </p>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.debtContent.howItWorksSteps[2]}
      </p>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.debtContent.monthlyIncome}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.debtContent.inputsTable.MonthlyIncome}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.debtContent.totalDebt}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.debtContent.inputsTable.TotalMonthlyDebt}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.debtContent.typesTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.debtContent.front}</strong>
          {textData.debtContent.typesList.frontEnd}
        </li>
        <li>
          <strong>{textData.debtContent.backend}</strong>{" "}
          {textData.debtContent.typesList.backEnd}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.debtContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.debtContent.benefitsList[0]}</li>
        <li>{textData.debtContent.benefitsList[1]}</li>
        <li>{textData.debtContent.benefitsList[2]}</li>
        <li>{textData.debtContent.benefitsList[3]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.debtContent.improveTitle}
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.debtContent.improveList[0]}</li>
        <li>{textData.debtContent.improveList[1]}</li>
        <li>{textData.debtContent.improveList[2]}</li>
        <li>{textData.debtContent.improveList[3]}</li>
      </ul>
      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.debtContent.classificationTitle}
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.debtContent.below} 20%:</strong>
          {textData.debtContent.classificationList[0]}
        </li>
        <li>
          <strong>20% - 35%:</strong>{" "}
          {textData.debtContent.classificationList[1]}
        </li>
        <li>
          <strong>36% - 42%:</strong>{" "}
          {textData.debtContent.classificationList[2]}
        </li>
        <li>
          <strong>{textData.debtContent.Above} 43%:</strong>{" "}
          {textData.debtContent.classificationList[3]}
        </li>
      </ul>
    </div>
  );
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  return (
    <>
      <Helmet>
        <title>Debt to Income Ratio Calculator | Know DTI Ratio Instantly</title>
        <meta
          name="description"
          content="Learn how to use the Online Debt to Income Ratio Calculator to know your Debt Ratio, understand your debt-to-income balance, and boost financial literacy."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/debt-to-income-ratio-calculator"
        />

        <meta
          property="og:title"
          content="Debt to Income Ratio Calculator | Know DTI Ratio Instantly"
        />
        <meta
          property="og:description"
          content="Learn how to use the Online Debt to Income Ratio Calculator to know your Debt Ratio, understand your debt-to-income balance, and boost financial literacy."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/debt-to-income-ratio-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="DTI Ratio Calculator Online - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        // generateData={result}
        calcDone={calcDone}
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.dtiCalc.pageTitlePart2}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.dtiCalc.pageTitlePart3}
            </span>
          </>
        }
        description={textData.dtiCalc.descriptionText}
        descriptionContent={descriptionContent}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start  text-black sm:text-sm text-sm">
                  {textData.dtiCalc.monthlyIncomeLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={10000}
                    max={500000}
                    // step={1000}
                    value={formik.values.monthlyIncome || 0}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        formik.values.monthlyIncome
                          ? ((formik.values.monthlyIncome - 10000) /
                              (500000 - 10000)) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col relative ">
                  {/* <div className="relative   w-full flex flex-col items-center"> */}
                  <Input
                    name="monthlyIncome"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.monthlyIncome)}
                    onChange={handleMonthlyIncomeChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.monthlyIncome &&
                      formik.errors.monthlyIncome
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]  ${
                      formik.touched.monthlyIncome &&
                      formik.errors.monthlyIncome
                        ? "border-danger"
                        : "border-gray-light"
                    } `}
                  />
                  {formik.touched.monthlyIncome &&
                    formik.errors.monthlyIncome && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.monthlyIncome}
                      </div>
                    )}
                  {/* </div> */}{" "}
                  <span className="absolute top-[7px] sm:top-[9px] right-[14px] text-gray-font text-sm sm:text-sm font-normal">
                    {textData.dtiCalc.perMonthSuffix}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start  text-black sm:text-sm text-sm">
                  {textData.dtiCalc.creditCardLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={creditSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={2000}
                    max={100000}
                    // step={2000}
                    value={formik.values.creditPay || 0}
                    onChange={handleCreditSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        formik.values.creditPay
                          ? ((formik.values.creditPay - 2000) /
                              (100000 - 2000)) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col relative">
                  <Input
                    name="creditPay"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.creditPay)}
                    onChange={handleCreditPayChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.creditPay && formik.errors.creditPay
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]   ${
                      formik.touched.creditPay && formik.errors.creditPay
                        ? "border-danger"
                        : "border-gray-light"
                    } `}
                  />
                  <span className="absolute top-[7px] sm:top-[9px] right-[14px] text-gray-font text-sm sm:text-sm font-normal">
                    {textData.dtiCalc.perMonthSuffix}
                  </span>

                  {formik.touched.creditPay && formik.errors.creditPay && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.creditPay}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start  text-black sm:text-sm text-sm">
                  {textData.dtiCalc.carLoanLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={carSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={5000}
                    max={100000}
                    // step={5000}
                    value={formik.values.carPay || 0}
                    onChange={handleCarSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        formik.values.carPay
                          ? ((formik.values.carPay - 5000) / (100000 - 5000)) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col relative   ">
                  <Input
                    name="carPay"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.carPay)}
                    onChange={handleCarPayChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.carPay && formik.errors.carPay
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]  ${
                      formik.touched.carPay && formik.errors.carPay
                        ? "border-danger"
                        : "border-gray-light"
                    } `}
                    // style={{
                    //   width: `${
                    //     formatToINR(formik.values.carPay).length * 10 + 60
                    //   }px`,
                    // }}
                  />
                  <span className="absolute top-[7px] sm:top-[9px] right-[14px] text-gray-font text-sm sm:text-sm font-normal">
                    {textData.dtiCalc.perMonthSuffix}
                  </span>

                  {formik.touched.carPay && formik.errors.carPay && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.carPay}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start  text-black sm:text-sm text-sm">
                  {textData.dtiCalc.studentLoanLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={studentSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={1000}
                    max={100000}
                    // step={1000}
                    value={formik.values.studentPay || 0}
                    onChange={handleStudentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        formik.values.studentPay
                          ? ((formik.values.studentPay - 1000) /
                              (100000 - 1000)) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col relative   ">
                  <Input
                    name="studentPay"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.studentPay)}
                    onChange={handleStudentPayChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.studentPay && formik.errors.studentPay
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]  ${
                      formik.touched.studentPay && formik.errors.studentPay
                        ? "border-danger"
                        : "border-gray-light"
                    } `}
                  />
                  <span className="absolute top-[7px] sm:top-[9px] right-[14px] text-gray-font text-sm sm:text-sm font-normal">
                    {textData.dtiCalc.perMonthSuffix}
                  </span>
                  {formik.touched.studentPay && formik.errors.studentPay && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.studentPay}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              {" "}
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default DtiCal;
